import { supabase } from "@/integrations/supabase/client";
import { getUserSettings } from "./userSettings";

interface SpeakOptions {
  text: string;
  language?: string;
  gender?: "male" | "female";
  rate?: number;
  /** When true, uses a calm neutral tone (higher stability, no style). For help/emergency. */
  neutral?: boolean;
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (error: Error) => void;
}

// Language code mapping for Web Speech API
const LANG_MAP: Record<string, string> = {
  he: "he-IL",
  en: "en-US",
  ar: "ar-SA",
  ru: "ru-RU",
  es: "es-ES",
  fr: "fr-FR",
};

// Detect language from text
export function detectLanguage(text: string): string {
  const hebrewRegex = /[\u0590-\u05FF]/;
  const arabicRegex = /[\u0600-\u06FF]/;
  const russianRegex = /[\u0400-\u04FF]/;
  
  if (hebrewRegex.test(text)) return "he";
  if (arabicRegex.test(text)) return "ar";
  if (russianRegex.test(text)) return "ru";
  return "en";
}

// Current audio element for stopping
let currentAudio: HTMLAudioElement | null = null;
// Playback generation token to cancel stale async playback flows
let playbackGeneration = 0;

const SILENT_WAV_DATA_URI =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";
const AUDIO_READY_TIMEOUT_MS = 1800;

function waitForAudioReady(audio: HTMLAudioElement, timeoutMs = AUDIO_READY_TIMEOUT_MS): Promise<void> {
  return new Promise((resolve, reject) => {
    let done = false;

    const cleanup = () => {
      audio.removeEventListener("canplay", onReady);
      audio.removeEventListener("loadeddata", onReady);
      audio.removeEventListener("error", onError);
      clearTimeout(timer);
    };

    const onReady = () => {
      if (done) return;
      done = true;
      cleanup();
      resolve();
    };

    const onError = () => {
      if (done) return;
      done = true;
      cleanup();
      reject(new Error("Audio failed to load"));
    };

    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      cleanup();
      reject(new Error("Audio load timeout"));
    }, timeoutMs);

    audio.addEventListener("canplay", onReady, { once: true });
    audio.addEventListener("loadeddata", onReady, { once: true });
    audio.addEventListener("error", onError, { once: true });
  });
}

// Stop any currently playing speech
export function stopSpeaking(): void {
  // Invalidate any in-flight async playback sequence
  playbackGeneration += 1;

  // Stop cloud audio
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.currentTime = 0;
    currentAudio.removeAttribute("src");
    currentAudio.load();
    currentAudio = null;
  }

  // Stop Web Speech API
  if ("speechSynthesis" in window) {
    window.speechSynthesis.cancel();
  }
}

// Web Speech API fallback
function speakWithWebSpeech(options: SpeakOptions): void {
  if (!("speechSynthesis" in window)) {
    options.onError?.(new Error("Speech synthesis not supported"));
    return;
  }

  window.speechSynthesis.cancel();
  
  const utterance = new SpeechSynthesisUtterance(options.text);
  const langCode = options.language || detectLanguage(options.text);
  utterance.lang = LANG_MAP[langCode] || LANG_MAP.en;
  utterance.rate = options.rate || 0.9;

  // Apply volume from settings (1-5 scale with wide perceptual range)
  const savedVolume = localStorage.getItem("ayuni_volume");
  const volumeLevel = savedVolume ? parseInt(savedVolume, 10) : 3;
  const VOLUME_MAP = [0.3, 0.5, 0.75, 0.9, 1.0];
  utterance.volume = VOLUME_MAP[Math.max(0, Math.min(4, volumeLevel - 1))];

  utterance.onstart = () => options.onStart?.();
  utterance.onend = () => options.onEnd?.();
  utterance.onerror = (e) => options.onError?.(new Error(e.error));

  window.speechSynthesis.speak(utterance);
}

// Main speak function - tries ElevenLabs first, falls back to Web Speech
export async function speak(options: SpeakOptions): Promise<void> {
  const { onStart, onEnd, onError } = options;
  let { text } = options;

  if (!text.trim()) return;

  // Stop any current speech and capture this playback generation
  stopSpeaking();
  const generation = playbackGeneration;

  // Get user settings for defaults
  const settings = getUserSettings();
  // Speech language: use explicit option > speechLanguage setting > auto-detect from text
  const speechLang = settings.speechLanguage || settings.language;
  const language = options.language || speechLang || detectLanguage(text);
  // Use explicit option > voiceGender setting > profile gender > default
  const profileGender = settings.gender === "male" || settings.gender === "female" ? settings.gender : null;
  const gender = options.gender || settings.voiceGender || profileGender || "female";
  const rate = options.rate ?? settings.speechRate ?? 0.9;

  // Check if muted
  if (settings.isMuted) {
    return;
  }

  // Create audio element immediately and unlock it with a tiny silent source.
  // This keeps playback in the user-gesture context on stricter browsers.
  const audio = new Audio();
  audio.preload = "auto";
  

  try {
    audio.muted = true;
    audio.src = SILENT_WAV_DATA_URI;
    await audio.play();
  } catch {
    // Unlock can fail on some browsers/contexts; we'll continue and still try normal playback.
  } finally {
    audio.pause();
    audio.currentTime = 0;
    audio.muted = false;
    audio.removeAttribute("src");
    audio.load();
  }

  if (generation !== playbackGeneration) return;
  currentAudio = audio;

  // If the text language differs from the target speech language, translate first
  const textLang = detectLanguage(text);
  if (textLang !== language) {
    try {
      const { data, error } = await supabase.functions.invoke("translate", {
        body: { text, targetLanguage: language },
      });
      if (generation !== playbackGeneration) return;
      if (!error && data?.translatedText) {
        text = data.translatedText;
      }
    } catch (e) {
      console.warn("TTS: Translation failed, speaking original text:", e);
    }
  }

  // Check if using cloned voice
  const useClonedVoice = settings.useClonedVoice && settings.clonedVoiceId;
  const clonedVoiceId = useClonedVoice ? settings.clonedVoiceId : null;

  try {
    // Try cloud TTS first
    const { data, error } = await supabase.functions.invoke("text-to-speech", {
      body: { text, language, gender, clonedVoiceId, speechRate: rate, neutral: options.neutral },
    });

    if (generation !== playbackGeneration) return;

    if (error) {
      console.warn("TTS edge function error, using fallback:", error);
      currentAudio = null;
      speakWithWebSpeech({ ...options, language, rate });
      return;
    }

    // Check if we should use fallback
    if (data.useFallback) {
      currentAudio = null;
      speakWithWebSpeech({ ...options, language, rate });
      return;
    }

    // Play audio using the pre-unlocked element
    if (data.audioContent) {
      audio.src = `data:audio/mpeg;base64,${data.audioContent}`;

      // Apply volume from settings (1-5 scale with wide perceptual range)
      const savedVolume = localStorage.getItem("ayuni_volume");
      const volumeLevel = savedVolume ? parseInt(savedVolume, 10) : 3;
      const VOLUME_MAP = [0.3, 0.5, 0.75, 0.9, 1.0];
      audio.volume = VOLUME_MAP[Math.max(0, Math.min(4, volumeLevel - 1))];

      audio.onplay = () => onStart?.();
      audio.onended = () => {
        if (currentAudio === audio) {
          currentAudio = null;
        }
        onEnd?.();
      };
      audio.onerror = () => {
        if (currentAudio === audio) {
          currentAudio = null;
        }
        console.warn("TTS: Audio playback failed, using fallback");
        speakWithWebSpeech({ ...options, language, rate });
      };

      audio.load();
      await waitForAudioReady(audio);
      if (generation !== playbackGeneration) return;

      audio.currentTime = 0;
      await audio.play();
    } else {
      // No audio content, use fallback
      currentAudio = null;
      speakWithWebSpeech({ ...options, language, rate });
    }
  } catch (error) {
    if (currentAudio === audio) {
      currentAudio = null;
    }
    console.warn("TTS: Error, using fallback:", error);
    speakWithWebSpeech({ ...options, language, rate });
    onError?.(error instanceof Error ? error : new Error("TTS error"));
  }
}

// Simple speak function without callbacks
export async function speakText(text: string, language?: string): Promise<void> {
  return speak({ text, language });
}
